// MARK://dhk8/admin-shell/pages/modules/DeviceDetailPage
// Ficha "ver-primero" de una impresora (Gustavo 2026-06-23). Header con nombre +
// estación/IP + pills (en línea/sin conexión, tickets/cocina) + Tabs (General,
// Historial). Entidad simple: solo General read-only + Historial.
//
// Carga por id: NO existe dev.getPrinter(id), así que traemos dev.listPrinters() y
// hacemos find(id) (mismo patrón que ProductDetailPage usa con las recetas).
//
// EDITAR: deshabilitado. El CRUD de impresoras NO existe en el backend real
// (dhk8-pos-printing solo expone plantillas/jobs; dev.updatePrinter lanza
// "Configuración de impresoras/KDS aún no disponible en el backend" salvo en
// PREVIEW_STUB). La lista (DevicesPage) ya deja sus acciones en "Próximamente"; la
// ficha replica esa nota en vez de abrir un sheet que fallaría al guardar.
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Pencil, Printer as PrinterIcon } from 'lucide-react'

import * as dev from '../../lib/clients/devices.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import StatusPill from '../../components/StatusPill.js'
import Tabs from '../../components/Tabs.js'
import AuditTrail from '../../components/AuditTrail.js'

type Tab = 'general' | 'historial'

export default function DeviceDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()

  // No hay dev.getPrinter(id): traemos la lista y hacemos find(id).
  const list = useAsync((s) => dev.listPrinters(s), [id])
  const [tab, setTab] = useState<Tab>('general')

  const p = useMemo(() => (list.data ?? []).find((x) => x.id === id) ?? null, [list.data, id])

  return (
    <div className="adm-page">
      <div className="adm-page__head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => navigate('/devices')} aria-label="Volver a impresoras / KDS"><ChevronLeft size={18} /></button>
          <div><h1 className="adm-page__title">Impresora</h1><p className="adm-page__sub">Impresoras / KDS · ficha del dispositivo</p></div>
        </div>
      </div>

      {asyncState(list.loading, list.error, list.reload)}

      {list.data && !p && (
        <div className="adm-empty">No se encontró esta impresora. Puede haber sido eliminada.</div>
      )}

      {p && (
        <EntityDetailPage
          title={p.name}
          subtitle={`${p.ip} · ${p.station}`}
          pills={<>
            <StatusPill tone={p.online ? 'ok' : 'danger'} label={p.online ? 'En línea' : 'Sin conexión'} />
            <StatusPill tone="neutral" label={p.kind === 'RECEIPT' ? 'Tickets' : 'Cocina'} />
          </>}
          actions={<button className="adm-btn adm-btn--primary adm-btn--sm" disabled title="Próximamente"><Pencil size={15} /> Editar</button>}
          tabs={<Tabs
            tabs={[
              { id: 'general', label: 'General' },
              { id: 'historial', label: 'Historial' },
            ]}
            active={tab}
            onChange={(t) => setTab(t as Tab)}
            ariaLabel="Secciones de la impresora"
          />}
        >
          {tab === 'general' && (
            <div className="adm-card adm-card--pad" style={{ display: 'grid', gap: 14, maxWidth: 560 }}>
              <Field label="Nombre"><span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><PrinterIcon size={16} /> {p.name}</span></Field>
              <Field label="Dirección IP"><span className="adm-mono">{p.ip}</span></Field>
              <Field label="Estación">{p.station || <span className="adm-muted">—</span>}</Field>
              <Field label="Tipo">{p.kind === 'RECEIPT' ? 'Tickets (recibo)' : 'Cocina (KDS)'}</Field>
              <Field label="Estado"><StatusPill tone={p.online ? 'ok' : 'danger'} label={p.online ? 'En línea' : 'Sin conexión'} /></Field>
              <p className="adm-row__meta" style={{ margin: 0 }}>Configuración de impresoras — próximamente. El backend aún no permite registrar, editar ni probar impresoras.</p>
            </div>
          )}

          {tab === 'historial' && <AuditTrail entityId={p.id} />}
        </EntityDetailPage>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--pos-fg-tertiary)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--pos-fg)' }}>{children}</div>
    </div>
  )
}
